/**
 * Prompt API Service
 * Handles secure, managed prompts instead of exposing raw prompt templates to the client
 */
import promptRegistry from './promptRegistry'

class PromptApiService {
  constructor() {
    this.baseUrl = process.env.VUE_APP_API_BASE_URL || 'https://api.getforge.com';
  }

  async callPrompt(promptId, promptArguments = {}, options = {}) {
    // Validate prompt ID exists in registry
    if (!promptRegistry.validatePromptId(promptId)) {
      throw new Error(`Invalid prompt ID: ${promptId}. Please check the prompt registry.`)
    }

    // Get prompt info from registry
    const promptInfo = promptRegistry.getPromptInfo(promptId)
    
    // Use registry response format if not specified in options
    const responseFormat = options.responseFormat || promptInfo.responseFormat || 'text'
    
    const payload = {
      chat: {
        prompt_id: promptId,
        arguments: promptArguments,
        response_json: options.responseJson || false,
        response_format: responseFormat,
        ...(options.versionNumber && { version_number: options.versionNumber })
      }
    };

    try {
      const response = await fetch(`${this.baseUrl}/api/v2/openai/prompt_completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Prompt API call failed:', error);
      throw error;
    }
  }

  getAuthToken() {
    if (window.store?.state?.user?.current?.sessionToken) {
      return window.store.state.user.current.sessionToken;
    }
    return localStorage.getItem('auth_token') || '';
  }

  handleError(error) {
    const errorInfo = {
      message: error.message,
      timestamp: new Date().toISOString(),
      type: 'prompt_api_error'
    };

    if (error.response) {
      errorInfo.status = error.response.status;
      errorInfo.statusText = error.response.statusText;
      errorInfo.data = error.response.data;
    }

    return errorInfo;
  }

  // Helper method to get prompt information
  getPromptInfo(promptId) {
    return promptRegistry.getPromptInfo(promptId)
  }

  // Helper method to validate arguments against prompt definition
  validateArguments(promptId, promptArgs) {
    const promptInfo = promptRegistry.getPromptInfo(promptId)
    const expectedArgs = promptInfo.arguments
    const providedArgs = Object.keys(promptArgs)
    
    const missingArgs = expectedArgs.filter(arg => !providedArgs.includes(arg))
    const extraArgs = providedArgs.filter(arg => !expectedArgs.includes(arg))
    
    return {
      isValid: missingArgs.length === 0,
      missing: missingArgs,
      extra: extraArgs,
      expected: expectedArgs,
      provided: providedArgs
    }
  }
}

export default new PromptApiService(); 